import { useCallback, useEffect, useState } from "react";
import { useAdminTranslation } from "../i18n/useAdminTranslation";

type VersionManifest = {
  sha: string;
  builtAt: string;
};

const LOCAL_SHA = __ADMIN_BUILD_SHA__;
const LOCAL_BUILT_AT = __ADMIN_BUILD_AT__;

async function fetchRemoteVersion(): Promise<VersionManifest | null> {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
    });
    if (!response.ok) {
      return null;
    }
    const data = (await response.json()) as Partial<VersionManifest>;
    if (typeof data.sha !== "string" || !data.sha) {
      return null;
    }
    return {
      sha: data.sha,
      builtAt: typeof data.builtAt === "string" ? data.builtAt : "",
    };
  } catch {
    return null;
  }
}

function formatBuiltAt(iso: string): string {
  if (!iso) {
    return "—";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString();
}

export function AdminBuildVersion() {
  const { t } = useAdminTranslation();
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [remoteBuiltAt, setRemoteBuiltAt] = useState(LOCAL_BUILT_AT);

  const checkForUpdate = useCallback(async () => {
    if (import.meta.env.DEV) {
      return;
    }
    const remote = await fetchRemoteVersion();
    if (!remote) {
      return;
    }
    setRemoteBuiltAt(remote.builtAt || LOCAL_BUILT_AT);
    setUpdateAvailable(remote.sha !== LOCAL_SHA);
  }, []);

  useEffect(() => {
    void checkForUpdate();

    const onFocus = () => {
      void checkForUpdate();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [checkForUpdate]);

  const builtAtLabel = formatBuiltAt(
    updateAvailable ? remoteBuiltAt : LOCAL_BUILT_AT
  );
  const label = updateAvailable
    ? t("buildVersion.updateAvailable", { sha: LOCAL_SHA })
    : t("buildVersion.current", { sha: LOCAL_SHA });
  const tooltip = updateAvailable
    ? t("buildVersion.tooltipUpdate", { builtAt: builtAtLabel })
    : t("buildVersion.tooltipCurrent", {
        sha: LOCAL_SHA,
        builtAt: builtAtLabel,
      });

  const className = updateAvailable
    ? "inline-flex items-center rounded-md px-2 py-0.5 font-mono text-[11px] font-medium tracking-tight bg-amber-50 text-amber-900 ring-1 ring-inset ring-amber-200/80 dark:bg-amber-950/50 dark:text-amber-100 dark:ring-amber-800 cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/60"
    : "inline-flex items-center rounded-md px-2 py-0.5 font-mono text-[11px] font-medium tracking-tight text-slate-500 dark:text-slate-400 ring-1 ring-inset ring-slate-200/80 dark:ring-slate-700";

  if (updateAvailable) {
    return (
      <button
        type="button"
        className={className}
        title={tooltip}
        aria-label={tooltip}
        onClick={() => window.location.reload()}
      >
        {label}
      </button>
    );
  }

  return (
    <span className={className} title={tooltip} aria-label={tooltip}>
      {label}
    </span>
  );
}
